'use client'

import { useEffect, useState } from 'react'
import { Sun, Moon } from 'lucide-react'

/**
 * Light/dark theme switch. Light is the default; dark is opt-in via the
 * `dark` class on <html>, persisted in localStorage and applied pre-paint
 * by /theme-init.js (no flash). State is read from the DOM after mount to
 * avoid hydration mismatch.
 */
export function ThemeToggle() {
  const [dark, setDark] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    setDark(document.documentElement.classList.contains('dark'))
  }, [])

  function toggle() {
    const next = !dark
    setDark(next)
    const root = document.documentElement
    if (next) root.classList.add('dark')
    else root.classList.remove('dark')
    try {
      localStorage.setItem('theme', next ? 'dark' : 'light')
    } catch {
      /* ignore persistence failure */
    }
  }

  // Stable placeholder until mounted (prevents SSR mismatch).
  if (!mounted) {
    return <span className="h-8 w-8" aria-hidden />
  }

  return (
    <button
      onClick={toggle}
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={dark ? 'Light theme' : 'Dark theme'}
      className="focus-ring flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-slate-100 text-slate-600 transition-colors hover:border-secondary/50 hover:text-secondary"
    >
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  )
}
